import { createStore, combineReducers, applyMiddleware } from 'redux';
import { thunk } from 'redux-thunk';
import { persistStore, persistReducer, createTransform } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import userPersistStorage from '../utils/userPersistStorage';
import schemaReducer from './reducers/schemaReducer';
import challengeReducer from './reducers/challengeReducer';
import authReducer from './reducers/authReducer';
import historyReducer from './reducers/historyReducer';
import sessionReducer from './reducers/sessionReducer';
import dbmsReducer from './reducers/dbmsReducer';
import userCommentReducer from './reducers/userCommentReducer';
// 브라우저 저장소에 남길 연결 필드(허용 목록) — 접속 정보·로컬 전용 연결은 남기지 않는다(2026-10-07)
import { persistableConnection, isLocalOnly } from './persistConnections';

/* 선별적 저장 옵션들 예시:

// 방법 1: 전체 설정으로 Reducer 단위 제어
const globalPersistConfig = {
    key: 'root',
    storage,
    whitelist: ['schema', 'auth'], // challenge는 제외
    blacklist: ['challenge'], // 또는 blacklist 사용
};

// 방법 2: transforms로 데이터 변환
const transformPersistConfig = {
    key: 'root',
    storage,
    transforms: [
        {
            in: (state, key) => { // 저장 시 민감정보 제거 },
            out: (state, key) => { // 복원 시 기본값 설정 }
        }
    ]
};
*/

// connections 배열 **안쪽**의 일시적 상태를 저장 대상에서 제외한다.
// whitelist 는 슬라이스 최상위 필드만 거르므로, connections[i].schemasError / isLoadingSchemas 는
// 그대로 저장돼 왔다. 그 결과 한 번 뜬 (Error) 표시가 새로고침 후에도 계속 남고,
// 새로고침 중 앱이 죽으면 isLoadingSchemas: true 가 굳어 "(Loading...)" 이 영구히 남는다.
// 과거 웹 모드 초기 상태에 있던 더미 연결. backend 에 없는 id 라서 남아 있으면
// "Connection config not found: conn_oracle_local_1" 을 유발한다. 소스에서는 제거했지만
// 이미 사용자 브라우저에 저장된 값이 rehydrate 로 되살아나므로, 복원 시점에도 걸러낸다.
const LEGACY_PLACEHOLDER_IDS = ['conn_oracle_local_1'];

const stripTransientConnectionFields = createTransform(
    (inbound, key) => {
        if (key !== 'connections' || !Array.isArray(inbound)) return inbound;
        return inbound
            .filter(c => !LEGACY_PLACEHOLDER_IDS.includes(c && c.id))
            .filter(c => !isLocalOnly(c))
            .map(c => ({
                ...persistableConnection(c),
                schemasError: null,
                isLoadingSchemas: false,
            }));
    },
    (outbound, key) => {
        if (key !== 'connections' || !Array.isArray(outbound)) return outbound;
        // 복원 경로 — 옛 저장본에는 더미와 굳어버린 에러/로딩 플래그가 들어 있을 수 있다.
        // 옛 저장본에 남은 접속 정보도 복원하지 않는다(다음 저장 때 지워진다)
        return outbound
            .filter(c => !LEGACY_PLACEHOLDER_IDS.includes(c && c.id))
            .filter(c => !isLocalOnly(c))
            .map(c => ({ ...persistableConnection(c), schemasError: null, isLoadingSchemas: false }));
    },
    { whitelist: ['connections'] }
);

// 개별 Reducer별 세밀한 persist 설정 (사용자별 storage 사용)
const schemaPersistConfig = {
    key: 'schema',
    storage: userPersistStorage, // 사용자별 storage 사용
    // schema에서 특정 필드만 저장
    whitelist: ['connections', 'tableComments', 'columnComments'], // 로딩 상태나 에러는 제외
    transforms: [stripTransientConnectionFields], // connections 내부의 에러/로딩 플래그까지 제거
};

const authPersistConfig = {
    key: 'auth',
    storage: storage, // auth는 기본 storage 사용 (로그인 정보는 전역)
    // auth에서 특정 필드만 저장
    blacklist: ['loading', 'error', 'sessionExpired', 'expiredUsrid'], // 로딩·에러·만료 표시는 제외 — 만료 표시가 남으면 새로고침 뒤에도 로그인 창이 작업 화면 위에 뜬다
};

const historyPersistConfig = {
    key: 'history',
    storage: userPersistStorage, // 사용자별 storage 사용
    // history 전체 저장 (쿼리 히스토리 유지)
    whitelist: ['queries'], // queries 배열만 저장
};

// challenge는 persist 하지 않음 (세션별로 새로 로드)
// sessions 도 persist 안 함 — backend 가 truth source 이고 매 세션 시작마다 fresh 폴링
const rootReducer = combineReducers({
    schema: persistReducer(schemaPersistConfig, schemaReducer),
    challenge: challengeReducer, // persist 하지 않음
    auth: persistReducer(authPersistConfig, authReducer),
    history: persistReducer(historyPersistConfig, historyReducer), // history persist 추가
    sessions: sessionReducer, // persist 하지 않음 — backend 폴링 결과
    dbms: dbmsReducer,        // persist 하지 않음 — 시작 시 backend /dbtypes 응답으로 채움
    userComments: userCommentReducer, // persist 하지 않음 — 로그인·연결 때 다시 받는다(사용자 주석)
    // 다른 리듀서들이 있다면 여기에 추가
});

// 이미 개별 설정된 리듀서는 root persist에서 제외 (이중 persist 방지)
const simplePersistConfig = {
    key: 'root',
    storage,
    blacklist: ['schema', 'auth', 'history', 'challenge', 'sessions', 'dbms', 'userComments'],
};

const persistedReducer = persistReducer(simplePersistConfig, rootReducer);

const store = createStore(
    persistedReducer,
    applyMiddleware(thunk)
);

export const persistor = persistStore(store);
export default store; 